import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useProjectSettings } from 'components/WorkspaceEditor/Workspace2Store'
import { UnitsOfMeasure } from 'models/projectSettings'
import { formatSelectionOption } from 'pages/ProjectsPage/WorkspacePage/components/Common/ContentItemElementText'
import { useMemo } from 'react'

/** Regular expression pattern to match and remove Units of Measure delimiters */
const UOM_DELIMITER_PATTERN = /\|START_IP\||\|END_IP\||\|START_SI\||\|END_SI\|/g

/** Constants for element types */
const ELEMENT_TYPE = {
    UNITS_OF_MEASURE: 'UnitsOfMeasure',
    TEXT: 'Text'
} as const

/** Return type for the useFormatReplacementOption hook */
interface UseFormatReplacementOptionReturn {
    /** The formatted text with child elements combined */
    formattedText: string
    /** Function to update content items with UOM elements */
    getUpdatedContentItem: (
        contentItem: Partial<TextContentElement>
    ) => Partial<TextContentElement>[]
    /** Function to remove UOM delimiters from text */
    removeUnitOfMeasureDelimiters: (text: string | undefined) => string
}

/**
 * Removes Units of Measure delimiter markers from text content.
 * These delimiters (|START_IP|, |END_IP|, |START_SI|, |END_SI|) are used internally
 * to mark imperial and metric unit boundaries but should not be displayed to users.
 *
 * @param text - The text content that may contain UOM delimiters
 * @returns The text with all UOM delimiters removed
 */
export const removeUnitOfMeasureDelimiters = (text: string | undefined): string => {
    return text?.replace(UOM_DELIMITER_PATTERN, '') || ''
}

/**
 * Processes a single UOM element by separating imperial and metric values.
 * 
 * @param element - The UOM element to process
 * @param remainingText - The remaining text to extract values from
 * @returns Updated element with separated values and remaining text
 */
const processUOMElement = (
    element: Partial<TextContentElement>, 
    remainingText: string
) => {
    if (element.type !== ELEMENT_TYPE.UNITS_OF_MEASURE || !remainingText || !element.imperialUnits) {
        return { element, remainingText }
    }
    
    const imperialEnd = remainingText.indexOf(element.imperialUnits) + element.imperialUnits.length
    const imperial = remainingText.slice(0, imperialEnd).trim()
    const metric = remainingText.replace(imperial, '').replace(/[^\w\s.]/gi, '').trim()
    
    return {
        element: { ...element, imperialUnits: imperial, metricUnits: metric },
        remainingText: remainingText.replace(imperial, '')
    }
}

/**
 * Processes elements within a replacement option that contain Units of Measure.
 * Simplified version that handles text and UOM elements efficiently.
 * 
 * @param contentItem - The content item containing elements to process
 * @returns Array of updated elements with properly separated values
 */
export const processUnitsOfMeasureElements = (
    contentItem: Partial<TextContentElement>
): Partial<TextContentElement>[] => {
    const elements = contentItem?.elements
    if (!elements?.some(el => el.type === ELEMENT_TYPE.UNITS_OF_MEASURE)) {
        return []
    }

    let remainingText = contentItem.text || ''
    const processedElements: Partial<TextContentElement>[] = []
    
    for (const element of elements) {
        if (element.type === ELEMENT_TYPE.TEXT && remainingText.includes(element.text!)) {
            remainingText = remainingText.replace(element.text!, '')
            processedElements.push(element)
        } else if (element.type === ELEMENT_TYPE.UNITS_OF_MEASURE) {
            const { element: updatedElement, remainingText: newRemainingText } = 
                processUOMElement(element, remainingText)
            processedElements.push(updatedElement)
            remainingText = newRemainingText
        }
    }

    return processedElements
}

/**
 * Processes replacement text by applying formatting and removing UOM delimiters.
 * 
 * @param contentItem - The content item to process
 * @param unitOfMeasure - The unit of measure preference
 * @returns Processed text string
 */
const processReplacementText = (
    contentItem: Partial<TextContentElement>, 
    unitOfMeasure: string
): string => {
    const formatted = formatSelectionOption(contentItem, unitOfMeasure.toLowerCase())
    return removeUnitOfMeasureDelimiters(formatted || contentItem.text?.trim() || '')
}

/**
 * Custom hook to format ReplacementOption text by combining main text with child elements.
 * Handles Units of Measure based on project settings and provides utilities for text processing.
 *
 * @param contentItem - The ReplacementOption content item to format
 * @returns Object containing formatted text and utility functions
 */
export const useFormatReplacementOption = (
    contentItem: Partial<TextContentElement>
): UseFormatReplacementOptionReturn => {
    const projectSettings = useProjectSettings()

    const formattedText = useMemo(() => {
        if (!contentItem) return ''

        try {
            const unitOfMeasure = projectSettings?.unitOfMeasure ?? UnitsOfMeasure.IMPERIAL
            return processReplacementText(contentItem, unitOfMeasure)
        } catch (error) {
            console.warn('Failed to format replacement option text:', error)
            return contentItem.text?.trim() || ''
        }
    }, [contentItem, projectSettings])

    return {
        formattedText,
        getUpdatedContentItem: processUnitsOfMeasureElements,
        removeUnitOfMeasureDelimiters,
    }
}
